/**
 * Run detail of the Test Runs screen (epic 031 task 06; FR-B29): the run
 * header (status, outcome, the test address to hand to the partner for
 * deletion — ADR-010 §12), the lead card, the rule verdicts, the "ESP actions:
 * skipped (test lead)" line (test leads never get esp_action rows — A15) and
 * the attempts table. `summary.error` / `summary.reason` surface as a banner
 * (e.g. "Test link expired after 30 minutes without a click").
 */

import type { TestRunDetail } from '@leads-platform/shared';
import { Banner } from '../../components/ui/banner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CopyButton } from '../../components/ui/copy-button';
import { StatusPill } from '../../components/ui/status-pill';
import { Table, TableBody, TableCell, TableRow } from '../../components/ui/table';
import { Tag } from '../../components/ui/tag';
import { fmtDate } from '../../lib/format';
import { parseRunSummary } from './run-summary';
import { AttemptsTable } from './attempts-table';

interface RunDetailProps {
  detail: TestRunDetail;
  /** Shown once live polling gave up after 10 minutes (the Refresh button). */
  timedOut?: boolean;
  onRefresh?: () => void;
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="mt-0.5 text-[13px] text-ink">{children}</div>
    </div>
  );
}

/** Test badge — the Leads screen's badge idiom (test leads are marked, §12). */
function TestBadge({ isTest }: { isTest: boolean }) {
  if (!isTest) return null;
  return (
    <span className="ml-2 rounded-full bg-amber/10 px-2 py-0.5 text-xs font-medium text-amber">
      Test
    </span>
  );
}

export function RunDetail({ detail, timedOut = false, onRefresh }: RunDetailProps) {
  const { run, lead, attempts } = detail;
  const summary = parseRunSummary(run.summary);
  const distributionStatus = lead?.processingState?.distribution?.status ?? null;
  const espStatus = lead?.processingState?.esp?.status ?? null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>
              {run.campaignName} — {run.mode === 'admin' ? 'admin test' : 'CTO test'}
            </CardTitle>
            <div className="mt-2 flex items-center gap-3">
              <StatusPill value={run.status} />
              {summary.outcome && (
                <Tag tone={summary.outcome === 'passed' ? 'accent' : 'rose'}>
                  {summary.outcome === 'passed' ? 'Passed' : 'Failed'}
                </Tag>
              )}
              {timedOut && onRefresh && (
                <Button variant="outline" size="sm" onClick={onRefresh}>
                  Refresh
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-faint">
              Test address
            </div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="break-all font-mono text-sm text-ink">{run.testEmail}</span>
              <CopyButton value={run.testEmail} label="Copy" title="Copy the test address" />
            </div>
            <p className="mt-1 text-xs text-muted">
              Give this address to the advertiser for deletion.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DetailField label="Created">
              <span className="font-mono text-xs">{fmtDate(run.createdAt)}</span>
            </DetailField>
            <DetailField label="Finished">
              <span className="font-mono text-xs">
                {run.finishedAt ? fmtDate(run.finishedAt) : '—'}
              </span>
            </DetailField>
          </div>
          {(summary.error || summary.reason) && (
            <Banner tone="warn" title="Test run failed.">
              {summary.error ?? summary.reason}
            </Banner>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Lead</CardTitle>
          </CardHeader>
          <CardContent>
            {lead ? (
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="break-all font-mono text-sm text-ink">{lead.emailOriginal}</span>
                  <TestBadge isTest={lead.isTest} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <DetailField label="Country">{lead.country ?? '—'}</DetailField>
                  <DetailField label="Beehiiv lookup">
                    {lead.beehiivLookupStatus ?? '—'}
                  </DetailField>
                  <DetailField label="Site ID">{lead.siteId ?? '—'}</DetailField>
                  <DetailField label="Sub ID">{lead.subId ?? '—'}</DetailField>
                  <DetailField label="Distribution">
                    {distributionStatus ?? 'not started'}
                  </DetailField>
                  <DetailField label="ESP actions">
                    {espStatus ? `${espStatus} (test lead)` : '—'}
                  </DetailField>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted">
                No lead yet — a CTO run links its lead when the test link is clicked.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rule verdicts</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.verdicts.length === 0 ? (
              <p className="text-sm text-muted">No verdicts recorded yet.</p>
            ) : (
              <Table>
                <TableBody>
                  {summary.verdicts.map((verdict) => (
                    <TableRow key={verdict.rule}>
                      <TableCell className="font-medium">{verdict.rule}</TableCell>
                      <TableCell className="w-24">
                        {verdict.pass ? (
                          <Tag tone="accent">Pass</Tag>
                        ) : (
                          <Tag tone="neutral">Skipped</Tag>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted">{verdict.reason ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Attempts</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <AttemptsTable attempts={attempts} />
        </CardContent>
      </Card>
    </div>
  );
}
